import dayjs, { type Dayjs } from 'dayjs';
import type { RouteExpense } from '../../../store/api/financeApi';
import type { CreateRouteExpenseRequest } from '../../../store/api/financeApi';
import { normalizeRouteExpenseType, type RouteExpenseType } from '../constants/routeExpenseTypes';

export type RouteExpenseFormValues = {
  type: RouteExpenseType;
  description: string;
  amountWithVAT: number | null;
  vatRate: number;
  expenseDate: Dayjs | null;
  invoiceId: number | null;
  comment: string;
};

export const DEFAULT_ROUTE_EXPENSE_FORM_VALUES: RouteExpenseFormValues = {
  type: 'OTHER',
  description: '',
  amountWithVAT: null,
  vatRate: 20,
  expenseDate: null,
  invoiceId: null,
  comment: ''
};

export function mergeRouteExpenseFormValues(
  partial?: Partial<RouteExpenseFormValues>
): RouteExpenseFormValues {
  return {
    ...DEFAULT_ROUTE_EXPENSE_FORM_VALUES,
    ...partial,
    expenseDate: partial?.expenseDate ?? dayjs()
  };
}

export function buildExpenseDescription(values: Pick<RouteExpenseFormValues, 'description' | 'comment'>): string | undefined {
  const desc = values.description.trim();
  const comment = values.comment.trim();
  if (desc && comment) return `${desc}\n${comment}`;
  return desc || comment || undefined;
}

export function mapRouteExpenseToFormValues(expense: RouteExpense): RouteExpenseFormValues {
  return {
    type: normalizeRouteExpenseType(expense.type),
    description: String(expense.description ?? '').trim(),
    amountWithVAT: expense.amountWithVAT != null ? Number(expense.amountWithVAT) : null,
    vatRate: Number(expense.vatRate ?? 20),
    expenseDate: expense.expenseDate ? dayjs(expense.expenseDate) : dayjs(),
    invoiceId: expense.invoiceId ?? null,
    comment: ''
  };
}

export function mapFormValuesToCreatePayload(values: RouteExpenseFormValues): CreateRouteExpenseRequest {
  return {
    type: values.type,
    description: buildExpenseDescription(values),
    amountWithVAT: Number(values.amountWithVAT),
    vatRate: values.vatRate,
    expenseDate: (values.expenseDate ?? dayjs()).format('YYYY-MM-DD'),
    ...(values.invoiceId != null ? { invoiceId: values.invoiceId } : {})
  };
}

export function mapFormValuesToUpdatePayload(
  values: RouteExpenseFormValues
): Partial<CreateRouteExpenseRequest> {
  return mapFormValuesToCreatePayload(values);
}
